/**
 * Offline Sync Hook
 * Handles online/offline detection and background sync
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import {
  initDB,
  getPendingSync,
  removePendingSync,
  saveItemsOffline,
  getItemsOffline,
  saveItemOffline,
  deleteItemOffline,
  addPendingSync,
  saveUserOffline,
  getUserOffline,
} from '@/lib/offlineDB';

export const useOfflineSync = (token) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const syncInProgress = useRef(false);

  // Initialize IndexedDB on mount
  useEffect(() => {
    initDB().catch(console.error);
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('Back online - starting sync');
      setIsOnline(true);
      // Trigger sync when coming back online
      if (token) {
        syncPendingChanges();
      }
    };

    const handleOffline = () => {
      console.log('Gone offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [token]);

  // Check pending sync count periodically
  useEffect(() => {
    const checkPending = async () => {
      try {
        const pending = await getPendingSync();
        setPendingCount(pending.length);
      } catch (error) {
        console.error('Failed to check pending sync:', error);
      }
    };

    checkPending();
    const interval = setInterval(checkPending, 5000);
    return () => clearInterval(interval);
  }, []);

  // Sync pending changes to server
  const syncPendingChanges = useCallback(async () => {
    if (!token || !isOnline || syncInProgress.current) return;

    syncInProgress.current = true;
    setIsSyncing(true);

    try {
      const pending = await getPendingSync();
      console.log(`Syncing ${pending.length} pending changes...`);

      for (const item of pending) {
        try {
          const headers = { Authorization: `Bearer ${token}` };

          switch (item.type) {
            case 'CREATE_ITEM':
              await axios.post(`${API}/items`, item.data, { headers });
              break;

            case 'UPDATE_ITEM':
              await axios.put(`${API}/items/${item.itemId}`, item.data, { headers });
              break;

            case 'DELETE_ITEM':
              await axios.delete(`${API}/items/${item.itemId}`, { headers });
              break;

            case 'COMPLETE_ITEM':
              await axios.put(`${API}/items/${item.itemId}/complete`, item.data, { headers });
              break;

            case 'ADD_COMMENT':
              await axios.post(`${API}/items/${item.itemId}/comments`, item.data, { headers });
              break;

            case 'ADD_REACTION':
              await axios.post(`${API}/items/${item.itemId}/reactions`, item.data, { headers });
              break;

            default:
              console.warn('Unknown sync type:', item.type);
          }

          // Remove from pending queue after successful sync
          await removePendingSync(item.id);
          console.log(`Synced: ${item.type}`);
        } catch (error) {
          console.error(`Failed to sync ${item.type}:`, error);
          // Keep in queue for retry (could add retry limit logic here)
        }
      }

      setLastSyncTime(new Date());
      
      // Refresh pending count
      const remaining = await getPendingSync();
      setPendingCount(remaining.length);

    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
      syncInProgress.current = false;
    }
  }, [token, isOnline]);

  // Cache items from server to IndexedDB
  const cacheItems = useCallback(async (items) => {
    try {
      await saveItemsOffline(items);
      console.log(`Cached ${items.length} items offline`);
    } catch (error) {
      console.error('Failed to cache items:', error);
    }
  }, []);

  // Get items (from cache if offline)
  const getItems = useCallback(async () => {
    if (!isOnline) {
      console.log('Offline - loading from cache');
      return await getItemsOffline();
    }
    return null; // Let caller fetch from API
  }, [isOnline]);

  // Queue a create operation
  const queueCreateItem = useCallback(async (itemData, tempId) => {
    // Save to local DB immediately
    const localItem = {
      ...itemData,
      id: tempId,
      _pendingSync: true,
      created_at: new Date().toISOString(),
    };
    await saveItemOffline(localItem);

    // Add to sync queue
    await addPendingSync({
      type: 'CREATE_ITEM',
      data: itemData,
      tempId,
    });

    return localItem;
  }, []);

  // Queue an update operation
  const queueUpdateItem = useCallback(async (itemId, itemData) => {
    // Update local DB
    const existingItems = await getItemsOffline();
    const existing = existingItems.find(i => i.id === itemId);
    if (existing) {
      await saveItemOffline({ ...existing, ...itemData, _pendingSync: true });
    }

    // Add to sync queue
    await addPendingSync({
      type: 'UPDATE_ITEM',
      itemId,
      data: itemData,
    });
  }, []);

  // Queue a delete operation
  const queueDeleteItem = useCallback(async (itemId) => {
    // Remove from local DB
    await deleteItemOffline(itemId);

    // Add to sync queue
    await addPendingSync({
      type: 'DELETE_ITEM',
      itemId,
    });
  }, []);

  // Queue a complete operation
  const queueCompleteItem = useCallback(async (itemId, completionData) => {
    // Update local DB
    const existingItems = await getItemsOffline();
    const existing = existingItems.find(i => i.id === itemId);
    if (existing) {
      await saveItemOffline({ 
        ...existing, 
        completed: true, 
        ...completionData,
        _pendingSync: true 
      });
    }

    // Add to sync queue
    await addPendingSync({
      type: 'COMPLETE_ITEM',
      itemId,
      data: completionData,
    });
  }, []);

  // Cache user data
  const cacheUser = useCallback(async (user) => {
    try {
      await saveUserOffline(user);
    } catch (error) {
      console.error('Failed to cache user:', error);
    }
  }, []);

  // Get cached user
  const getCachedUser = useCallback(async () => {
    try {
      return await getUserOffline();
    } catch (error) {
      console.error('Failed to get cached user:', error);
      return null;
    }
  }, []);

  return {
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncTime,
    syncPendingChanges,
    cacheItems,
    getItems,
    queueCreateItem,
    queueUpdateItem,
    queueDeleteItem,
    queueCompleteItem,
    cacheUser,
    getCachedUser,
  };
};

export default useOfflineSync;
